import React, { useCallback } from 'react'
import { DefaultTheme, StyledComponent } from 'styled-components'
import * as Styled from './Pagination.styles'
import { DOTS, FIRST_PAGE } from './Pagination.consts'
import { PageNumber, PaginationData } from './Pagination.types'
import usePagination from './Pagination.hooks'

export interface ComponentProps extends PaginationData {
  /**
   * Custom container for the pagination
   */
  container?: StyledComponent<React.ElementType, DefaultTheme> | React.ElementType
}

const Pagination: React.FC<ComponentProps> = React.forwardRef<HTMLDivElement, ComponentProps>((props, ref) => {
  const { container, ...paginationProps } = props
  const { range, current, setPage, next: handleNext, previous: handlePrevious } = usePagination(paginationProps)
  const isEnoughSpaceForDirectionButtons = range.length < 5

  const renderPageButton = useCallback(
    (pageNumber: PageNumber, index) =>
      pageNumber === DOTS ? (
        <Styled.DotsButton tabIndex={-1} key={`${current}${index}`}>
          &hellip;
        </Styled.DotsButton>
      ) : (
        <Styled.PageButton isCurrent={pageNumber === current} onClick={() => setPage(pageNumber)} key={`${current}${index}`}>
          {pageNumber}
        </Styled.PageButton>
      ),
    [current, setPage],
  )

  // Don't show navigation if there is only 1 page
  if (paginationProps.total <= 1) {
    return null
  }

  return (
    <Styled.Container as={container} ref={ref}>
      <Styled.DirectionButton
        disabled={current <= FIRST_PAGE + 1}
        onClick={handlePrevious}
        isVisibleOnBreakpointSm={isEnoughSpaceForDirectionButtons}>
        Previous
      </Styled.DirectionButton>

      <Styled.List>{range.map(renderPageButton)}</Styled.List>

      <Styled.DirectionButton
        disabled={current >= paginationProps.total}
        onClick={handleNext}
        isVisibleOnBreakpointSm={isEnoughSpaceForDirectionButtons}>
        Next
      </Styled.DirectionButton>
    </Styled.Container>
  )
})

Pagination.displayName = 'Pagination'
export default Pagination
